/**
 * AI Quick Actions Bar
 *
 * Horizontal button bar for common AI content operations.
 * Saves a content version before replacing content.
 */

import { useState } from 'react';
import { Button, Space, Popover, Typography, message, Spin } from 'antd';
import {
  ClearOutlined,
  OrderedListOutlined,
  CheckSquareOutlined,
  QuestionCircleOutlined,
  MessageOutlined,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { aiApi } from '@api/ai';

const { Text, Paragraph } = Typography;

interface AIQuickActionsProps {
  content: string;
  onContentReplace: (newContent: string, source: string) => void;
  onOpenChat?: () => void;
  disabled?: boolean;
}

const ACTIONS = [
  { key: 'content_cleanup', label: 'Clean up', icon: <ClearOutlined /> },
  { key: 'content_outline', label: 'Outline', icon: <OrderedListOutlined /> },
  { key: 'content_checklist', label: 'Checklist', icon: <CheckSquareOutlined /> },
  { key: 'content_gaps', label: 'Missing?', icon: <QuestionCircleOutlined />, isAnalysis: true },
];

export default function AIQuickActions({
  content,
  onContentReplace,
  onOpenChat,
  disabled,
}: AIQuickActionsProps) {
  const [gapsResult, setGapsResult] = useState<string | null>(null);

  const executeMutation = useMutation({
    mutationFn: ({ taskKey }: { taskKey: string; isAnalysis?: boolean }) =>
      aiApi.executeTask(taskKey, { variables: { content } }),
    onSuccess: (result, { isAnalysis }) => {
      if (!result.success) {
        message.error(result.error || 'AI task failed');
        return;
      }
      if (isAnalysis) {
        setGapsResult(result.content);
      } else {
        onContentReplace(result.content, 'ai');
        message.success('Content updated by AI');
      }
    },
    onError: () => {
      message.error('AI task failed');
    },
  });

  const handleAction = (key: string, isAnalysis = false) => {
    if (!content?.trim()) {
      message.warning('No content to process');
      return;
    }
    executeMutation.mutate({ taskKey: key, isAnalysis });
  };

  const isLoading = executeMutation.isPending;

  return (
    <Space size={4} wrap style={{ marginTop: 4 }}>
      {ACTIONS.map((action) =>
        action.isAnalysis ? (
          <Popover
            key={action.key}
            title="Analysis"
            content={
              gapsResult ? (
                <Paragraph style={{ maxWidth: 350, whiteSpace: 'pre-wrap', margin: 0 }}>
                  {gapsResult}
                </Paragraph>
              ) : (
                <Text type="secondary">Click to analyze</Text>
              )
            }
            trigger="click"
            onOpenChange={(open) => {
              if (open && !gapsResult) {
                handleAction(action.key, true);
              }
            }}
          >
            <Button
              size="small"
              type="text"
              icon={action.icon}
              loading={isLoading && executeMutation.variables?.taskKey === action.key}
              disabled={disabled || !content?.trim()}
              style={{ fontSize: 12 }}
            >
              {action.label}
            </Button>
          </Popover>
        ) : (
          <Button
            key={action.key}
            size="small"
            type="text"
            icon={action.icon}
            onClick={() => handleAction(action.key)}
            loading={isLoading && executeMutation.variables?.taskKey === action.key}
            disabled={disabled || isLoading || !content?.trim()}
            style={{ fontSize: 12 }}
          >
            {action.label}
          </Button>
        )
      )}
      {onOpenChat && (
        <Button
          size="small"
          type="text"
          icon={<MessageOutlined />}
          onClick={onOpenChat}
          disabled={disabled}
          style={{ fontSize: 12 }}
        >
          Chat
        </Button>
      )}
      {isLoading && <Spin size="small" />}
    </Space>
  );
}
